/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useEffect, useRef, useState } from 'react'
import ScrollLogo from './ScrollLogo.jsx'

const BUTTONS = [
  { key: 'btn1', cls: 'but-1', href: 'https://sequoia.fitness/about-sequoia/', blank: true, label: 'درباره‌ی ما' },
  { key: 'btn2', cls: 'but-2', href: '#pricing', blank: false, label: 'دریافت برنامه' },
  { key: 'btn3', cls: 'but-3', href: '/academy', blank: false, label: 'آکادمی سکویا فیتنس' },
]
const DEFAULT_ACTIVE = 'btn2'

const clamp = (v, a, b) => Math.max(a, Math.min(b, v))
const lerp = (a, b, t) => a + (b - a) * t
const hexToRgb = (hex) => {
  const h = hex.replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}
const rgbToHex = ({ r, g, b }) => '#' + [r, g, b].map((x) => Math.round(clamp(x, 0, 255)).toString(16).padStart(2, '0')).join('')
const mixHex = (a, b, t) => {
  const x = hexToRgb(a)
  const y = hexToRgb(b)
  return rgbToHex({ r: lerp(x.r, y.r, t), g: lerp(x.g, y.g, t), b: lerp(x.b, y.b, t) })
}
const setStop = (el, offset, color, opacity) => {
  el.setAttribute('offset', offset.toFixed(2) + '%')
  el.setAttribute('stop-color', color)
  el.setAttribute('stop-opacity', opacity.toFixed(3))
}

function useScrollLogo(wrapRef) {
  useEffect(() => {
    const wrap = wrapRef.current
    const svg = wrap && wrap.querySelector('svg')
    const q = (id) => svg && svg.querySelector('#' + id)
    const stops = ['logoStopTop', 'logoStopA', 'logoStopB', 'logoStopC', 'logoStopD', 'logoStopBottom'].map(q)
    const shadow = q('logoShadowNode')
    if (!wrap || !svg || !shadow || stops.some((s) => !s)) return undefined
    const [top, a, b, c, d, bottom] = stops
    const update = () => {
      const rect = wrap.getBoundingClientRect()
      const vh = window.innerHeight || document.documentElement.clientHeight
      const start = vh - rect.height
      const end = 0.1 * vh
      let p = rect.top >= start ? 0 : rect.top <= end ? 1 : (start - rect.top) / (start - end)
      p = clamp(p, 0, 1)
      const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
      const mid = lerp(30, 100, e)
      const L = [0, clamp(mid - 14, 0, 100), clamp(mid - 8, 0, 100), clamp(mid, 0, 100), clamp(mid + 8, 0, 100), 100]
      for (let i = 1; i < L.length; i++) if (L[i] < L[i - 1]) L[i] = L[i - 1]
      setStop(top, L[0], '#1f504c', 1)
      setStop(a, L[1], '#1f504c', 1)
      setStop(b, L[2], '#1f504c', lerp(0.72, 0.58, e))
      setStop(c, L[3], '#ffffff', lerp(0.55, 0.4, e))
      setStop(d, L[4], '#ffffff', lerp(0.85, 0.7, e))
      setStop(bottom, L[5], '#ffffff', 1)
      shadow.setAttribute('flood-color', mixHex('#000000', '#e06d01', e))
      shadow.setAttribute('flood-opacity', lerp(0.7, 1, e).toFixed(4))
      shadow.setAttribute('stdDeviation', lerp(6, 12, e).toFixed(2))
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [wrapRef])
}

export default function About() {
  const logoRef = useRef(null)
  const [active, setActive] = useState(DEFAULT_ACTIVE)
  const boxRef = useRef(null)
  useScrollLogo(logoRef)

  useEffect(() => {
    const onDown = (e) => {
      if (e.pointerType === 'touch' && boxRef.current && !boxRef.current.contains(e.target)) setActive(DEFAULT_ACTIVE)
    }
    document.addEventListener('pointerdown', onDown, { passive: true })
    return () => document.removeEventListener('pointerdown', onDown)
  }, [])

  return (
    <div className="lp-about" id="scroll-here">
      <div className="lp-about-in">
        <div className="lp-about-title">
          <h2 className="lp-about-h2">
            ر<span></span>شـــد کـــن و پایدار بمون
          </h2>
        </div>
        <div className="lp-about-body">
          <div className="lp-about-logo">
            <div className="lp-about-logo-w">
              <div className="lp-about-logo-in">
                <div id="scroll-svg-color" ref={logoRef}>
                  <ScrollLogo className="lp-about-logo-svg" />
                </div>
              </div>
            </div>
          </div>
          <div id="hp-top-sec" className="lp-about-card" ref={boxRef} onMouseLeave={() => setActive(DEFAULT_ACTIVE)}>
            <div className="lp-about-text">
              <div className="lp-about-txt-in">
                <p className="lp-about-p">سکویاها الهام‌بخش رشد و پایداری هستن. ما هم دوست داریم مثل این درخت‌ها در هر شرایطی رشد کنیم و ثبات داشته باشیم.</p>
                <p className="lp-about-p">سال‌ها فعالیت تو باشگاه و استودیو به ما نشون داد که خیلی‌ها انگیزه دارن، اما مسیر رو پیدا نمی‌کنن یا بهش پایبند نمی‌مونن... کمبود وقت، شلوغی باشگاه، برنامه‌های نامناسب، یا تمرین‌هایی که نه نتیجه دارن و نه قابل ادامه‌ان.</p>
                <p className="lp-about-p">برای همین تصمیم گرفتیم این تجربه رو به شکل یه پلتفرم آنلاین دربیاریم؛ جایی که بتونی بدون محدودیت زمانی و مکانی، یه برنامه‌ی اصولی داشته باشی، پیشرفتت رو ببینی و بدون سردرگمی جلو بری.</p>
              </div>
            </div>
            {BUTTONS.map((b) => (
              <a
                key={b.key}
                className={`lp-${b.key} hp-top-sec ${b.cls}${active === b.key ? ' is-active' : ''}`}
                href={b.href}
                {...(b.blank ? { target: '_blank', rel: 'noreferrer' } : {})}
                onMouseEnter={() => setActive(b.key)}
                onPointerDown={(e) => {
                  if (e.pointerType === 'touch') setActive(b.key)
                }}
              >
                <div className={`lp-${b.key}-t`}>
                  <h2 className={`lp-${b.key}-h2`}>{b.label}</h2>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
